import { fmtRange, formatDays, leaveYearOf } from '../lib/dates';
import { ACTIVE_STATUSES, balanceFor, canDecide, displayType, partSuffix, requestDays, typeOf } from '../lib/leave';
import { managerIds } from '../lib/orgchart';
import { useStore } from '../store/StoreProvider';
import { useCtx } from '../store/useCtx';
import type { LeaveRequest } from '../types';
import { Avatar, EmployeeName, StatusChip, firstName, typeStyle } from '../ui/bits';
import { useDialogs } from '../ui/dialogs';
import { Icon } from '../ui/Icon';
import { useRun } from '../ui/Toasts';

export function Approvals() {
  const ctx = useCtx();
  const store = useStore();
  const run = useRun();
  const { openDetail } = useDialogs();

  const managers = managerIds(ctx.users);
  const decidable = ctx.requests.filter((r) => canDecide(ctx.me, r, ctx));
  const pending = decidable.filter((r) => r.status === 'pending').sort((a, b) => a.start.localeCompare(b.start));
  const history = decidable
    .filter((r) => r.status !== 'pending' && r.userId !== ctx.me.id)
    .sort((a, b) => (b.decidedAt || b.createdAt) - (a.decidedAt || a.createdAt))
    .slice(0, 12);

  const card = (r: LeaveRequest) => {
    const u = ctx.userById.get(r.userId);
    const t = typeOf(r.typeId);
    const dept = u?.departmentId ? ctx.deptById.get(u.departmentId) : undefined;
    const days = requestDays(r, ctx.bh);
    const part = partSuffix(r);
    const b = t.deducts && u ? balanceFor(u, leaveYearOf(r.start, ctx.settings.leaveYearStartMonth), ctx) : null;
    // "Same team" = the same department, or the same manager.
    const sameTeam = (other?: typeof u) =>
      !!u && !!other && other.id !== u.id
      && ((!!u.departmentId && other.departmentId === u.departmentId) || (!!managers.get(u.id) && managers.get(other.id) === managers.get(u.id)));
    const clashes = ctx.requests.filter(
      (o) => o.id !== r.id && ACTIVE_STATUSES.includes(o.status) && sameTeam(ctx.userById.get(o.userId)) && o.start <= r.end && o.end >= r.start,
    );
    return (
      <li key={r.id} className="card approval">
        <div className="approval-who">
          <Avatar name={u?.name} photoUrl={u?.photoUrl} className="avatar-lg" />
          <div>
            <strong>{u?.name}</strong>
            <span className="muted">{dept?.name || 'No department'}</span>
          </div>
        </div>
        <div className="approval-what">
          <span className="type-dot" style={typeStyle(t)}><Icon name={t.icon} size={16} /></span>
          <div>
            <strong>{t.label}: {fmtRange(r.start, r.end)}</strong>
            <span className="muted">{formatDays(days)} working day{days === 1 ? '' : 's'}{part ? ` · ${part}` : ''}</span>
          </div>
        </div>
        <div className="approval-meta">
          {r.note && <p className="wrap">“{r.note}”</p>}
          {b && u && (
            <p className={b.remaining < 0 ? 'text-warn' : 'muted'}>
              {b.remaining < 0 && <Icon name="alert" size={14} />} Leaves {firstName(u.name)} with {formatDays(b.remaining)} of {formatDays(b.allowance)} days.
            </p>
          )}
          {clashes.length > 0 && (
            <p className="text-warn">
              <Icon name="alert" size={14} /> Also off from the same team:{' '}
              {clashes.map((o) => `${ctx.userById.get(o.userId)?.name} (${fmtRange(o.start, o.end)})`).join(', ')}
            </p>
          )}
        </div>
        <div className="approval-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDetail(r.id)}>Decline…</button>
          <button type="button" className="btn btn-sm" onClick={() => run(store.decideRequest(r.id, 'approved'), 'Approved.')}>
            <Icon name="check" size={16} />
            Approve
          </button>
        </div>
      </li>
    );
  };

  return (
    <>
      <section aria-labelledby="pend-title">
        <h2 id="pend-title" className="section-title">Waiting for you <span className="count-pill">{pending.length}</span></h2>
        {pending.length ? (
          <ul className="stack">{pending.map(card)}</ul>
        ) : (
          <div className="card empty-state">
            <Icon name="check-circle" size={32} />
            <p><strong>All caught up.</strong><br /><span className="muted">New requests from your team will appear here.</span></p>
          </div>
        )}
      </section>

      {history.length > 0 && (
        <section aria-labelledby="hist-title">
          <h2 id="hist-title" className="section-title">Recent decisions</h2>
          <div className="card table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Person</th><th scope="col">Leave</th><th scope="col">Status</th>
                  <th scope="col"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id}>
                    <td><EmployeeName user={ctx.userById.get(r.userId)} /></td>
                    <td>{displayType(r, ctx.me, ctx).label}<br /><span className="muted">{fmtRange(r.start, r.end)}</span></td>
                    <td><StatusChip status={r.status} /></td>
                    <td className="right"><button type="button" className="btn btn-secondary btn-sm" onClick={() => openDetail(r.id)}>Details</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
